import {
  Button,
  DatePicker,
  Dropdown,
  ErrorContainer,
  InputField,
  LabelComponent,
  ModalPopup,
  Stack,
  Title
} from 'components';
import { TextAreaInputField } from 'components/inputField/textAreaInpuField';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { getLsUserRoleObject } from 'modules/networkTools/localStorageTokens';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getClientNotes } from 'store/clientProfile/clientProfileActions';
import {
  getAllManagers,
  getAllCHWs,
  postUpdateNewManagerReview,
  getBillingProviders
} from 'store/clients/clientsActions';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import * as yup from 'yup';

import Avatar from '../screener/components/avatar';
import style from './newManagerReview.module.scss';

const ManagerReviewSchema = yup.object().shape({
  reviewDate: yup
    .string()
    .required('Date is required.')
    .test('not-future', 'Date cannot be in the future.', (value) => {
      if (!value) return true;
      return dayjs(value).isSameOrBefore(dayjs(), 'day');
    }),
  timeSpent: yup.string().required('Time Spent (min) is required.'),
  managerId: yup.string().required('Manager is required.'),
  chwId: yup.string().required('CHW is required.'),
  documentationTime: yup
    .string()
    .required('Documentation Time (min) is required.')
    .test('documentationTime', 'Documentation time is required.', (value) => {
      return value !== null && value !== '' && parseInt(value ?? '0') > 0;
    }),
  note: yup.string().required('Note is required.')
});

const ProviderSchema = yup.object().shape({
  billingProviderId: yup.string().required('Provider is required.')
});

const useGetNotes = () => {
  const { clientId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const noteTypes = searchParams.get('noteTypes') ?? undefined;

  return () =>
    dispatch(getClientNotes({ clientId, page: 0, size: 5, firstRender: true, types: noteTypes }));
};

const NewManagerReview = () => {
  const { clientId = '' } = useParams();
  const dispatch = useAppDispatch();
  const managerReview = useAppSelector((state) => state.clients.client?.data?.managerReview);
  const isBillingEnabled = useAppSelector((state) => state.clients.client?.data?.enableBilling);
  const chwId = useAppSelector((state) => state.clients.client?.data?.chwId);
  const [open, setOpen] = useState(false);
  const getNotes = useGetNotes();
  const managers = useAppSelector((state) => state.clients.managers.data);
  const chws = useAppSelector((state) => state.clients.chws.data);
  const billingProviders = useAppSelector((state) => state.clients.billingProviders.data).filter(
    (p) => p.id !== -1
  );
  const userRole = getLsUserRoleObject();
  const userId = userRole.userId;
  const organizationId = userRole.organizationId;
  const clientChw = chws.find((c) => c.id.toString() === chwId?.toString());
  const currentManager = managers.find((manager) => manager.id.toString() === userId.toString());

  useEffect(() => {
    dispatch(getAllManagers({ organizationId }));
    dispatch(getAllCHWs({ organizationId }));
    dispatch(getBillingProviders({ organizationId }));
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      reviewDate: '',
      timeSpent: '10',
      managerId: currentManager?.id.toString() || '',
      chwId: clientChw?.id.toString() || '',
      billingProviderId: '',
      documentationTime: '1',
      note: "CHW reviewed client's applicable history, changes since last staffing, goals, and interventions to be used to help the client achieve next steps with supervisor."
    },
    validateOnMount: true,
    validationSchema: isBillingEnabled
      ? ManagerReviewSchema.concat(ProviderSchema)
      : ManagerReviewSchema,
    onSubmit: async (values, helpers) => {
      await dispatch(
        postUpdateNewManagerReview({
          reviewDate: values.reviewDate,
          timeSpent: Number(values.timeSpent),
          managerId: values.managerId,
          chwId: values.chwId,
          billingProviderId: values.billingProviderId,
          documentationTime: values.documentationTime,
          note: values.note,
          clientId
        })
      );

      getNotes();

      helpers.resetForm();

      setOpen(false);
    }
  });

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    formik.resetForm();
    setOpen(false);
  };

  return (
    <>
      <Button
        size="extraSmall"
        handleOnClick={handleOpen}
        text="Manager Review"
        theme="transparent"
        customStyleClass={style.managerReview}
      />
      <ModalPopup customClassInsideContainer={style.modal} open={open}>
        <Stack width="100%">
          <form onSubmit={formik.handleSubmit}>
            <Stack width="100%" display="flex" flexDirection="column" gap="16px">
              <Stack
                width="100%"
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Title type="h3" text="Manager Review" />
                <button className={style.closeIcon} onClick={handleClose}>
                  <span className="material-icons">close</span>
                </button>
              </Stack>
              <Stack display="flex" flexDirection="column" gap="4px">
                <Stack display="flex" flexDirection="row" gap="16px">
                  <Stack width="50%">
                    <LabelComponent text="Date" mandatory />
                    <DatePicker
                      value={formik.values.reviewDate}
                      onChange={(date: string) => {
                        formik.setFieldValue('reviewDate', date).then(() => {
                          formik.setFieldTouched('reviewDate', true);
                        });
                      }}
                      onBlur={() => formik.setFieldTouched('reviewDate', true)}
                      maxDate={dayjs()}
                    />
                    <ErrorContainer
                      visible={formik.touched.reviewDate && formik.errors.reviewDate}
                      errors={formik.errors}
                      name="reviewDate"
                    />
                  </Stack>
                  <Stack width="50%">
                    <LabelComponent text="Time Spent (min)" mandatory />
                    <InputField
                      value={formik.values.timeSpent}
                      onChange={(e) => {
                        // only allow numbers
                        if (e.target.value.match(/^[0-9]*$/)) {
                          formik.handleChange(e);
                        }
                      }}
                      onBlur={formik.handleBlur}
                      placeholder="0"
                      name="timeSpent"
                    />
                    <ErrorContainer
                      visible={formik.touched.timeSpent && formik.errors.timeSpent}
                      errors={formik.errors}
                      name="timeSpent"
                    />
                  </Stack>
                </Stack>
                <Stack width="100%" display="flex" flexDirection="row" gap="16px">
                  <Stack width="50%">
                    <LabelComponent text="Manager" mandatory />
                    <Dropdown
                      label="Select"
                      data={managers.map((manager) => ({
                        id: manager.id.toString(),
                        name: manager.name,
                        category: manager.active ? 'ACTIVE' : 'INACTIVE'
                      }))}
                      value={formik.values.managerId}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      name="managerId"
                      categoryList={[
                        { id: 'ACTIVE', label: 'Active' },
                        { id: 'INACTIVE', label: 'Inactive' }
                      ]}
                    />
                    <ErrorContainer
                      visible={formik.touched.managerId && formik.errors.managerId}
                      errors={formik.errors}
                      name="managerId"
                    />
                  </Stack>
                  <Stack width="50%">
                    <LabelComponent text="CHW" mandatory />
                    <Dropdown
                      label="Select"
                      data={chws.map((chw) => ({
                        id: chw.id.toString(),
                        name: chw.name,
                        category: chw.active ? 'ACTIVE' : 'INACTIVE'
                      }))}
                      categoryList={[
                        { id: 'ACTIVE', label: 'Active' },
                        { id: 'INACTIVE', label: 'Inactive' }
                      ]}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      name="chwId"
                      value={formik.values.chwId}
                    />
                    <ErrorContainer
                      visible={formik.touched.chwId && formik.errors.chwId}
                      errors={formik.errors}
                      name="chwId"
                    />
                  </Stack>
                </Stack>
                <Stack width="100%" display="flex" flexDirection="row" gap="16px">
                  <Stack width="50%">
                    <LabelComponent text="Provider" mandatory={isBillingEnabled} />
                    <Dropdown
                      data={billingProviders}
                      label="Select"
                      name="billingProviderId"
                      value={formik.values.billingProviderId}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <ErrorContainer
                      errors={formik.errors}
                      visible={formik.touched.billingProviderId && formik.errors.billingProviderId}
                      name="billingProviderId"
                    />
                  </Stack>
                  <Stack width="50%">
                    <LabelComponent text="Documentation Time (min)" mandatory />
                    <InputField
                      placeholder="0"
                      name="documentationTime"
                      value={formik.values.documentationTime}
                      onChange={(e) => {
                        // only allow numbers
                        if (e.target.value.match(/^[0-9]*$/)) {
                          formik.handleChange(e);
                        }
                      }}
                      onBlur={formik.handleBlur}
                    />
                    <ErrorContainer
                      errors={formik.errors}
                      visible={formik.touched.documentationTime && formik.errors.documentationTime}
                      name="documentationTime"
                    />
                  </Stack>
                </Stack>
                <Stack width="100%">
                  <LabelComponent text="Note" mandatory />
                  <TextAreaInputField
                    rowsNumber={4}
                    placeholder="Type here"
                    value={formik.values.note}
                    name="note"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  <ErrorContainer
                    errors={formik.errors}
                    visible={formik.touched.note && formik.errors.note}
                    name="note"
                  />
                </Stack>
              </Stack>
              <Stack display="flex" flexDirection="row" justifyContent="space-between">
                {managerReview?.lastReviewDate && (
                  <Stack
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    gap="8px"
                    flexGrow={1}
                  >
                    <p className={style.lastReview}>
                      Last review: {dayjs(managerReview.lastReviewDate).format('MM/DD/YYYY')}
                    </p>
                    <Stack
                      className={style.author}
                      display="flex"
                      flexDirection="row"
                      alignItems="center"
                      gap="4px"
                    >
                      <Avatar imageUrl={managerReview.authorImage} />
                      <p>{managerReview.authorName}</p>
                    </Stack>
                  </Stack>
                )}
                <Stack width="128px">
                  <Button
                    disabled={!formik.isValid || formik.isSubmitting}
                    theme="primary"
                    text="Final"
                    type="submit"
                  />
                </Stack>
              </Stack>
            </Stack>
          </form>
        </Stack>
      </ModalPopup>
    </>
  );
};

export default NewManagerReview;
